import { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { useFacilityProvider } from 'lib/hooks/queries/practice/useFacilityProvider';

import CreateUpdateFacilityProvider from 'components/admin/pages/Practices/Facility/components/CreateUpdateFacilityProvider';

import AgTablePage from '../../../AgTablePage/AgTablePage';
import EmptyDataRows from '../../../AgTablePage/components/EmptyDataRows';
import SubstepConfirmationModal from '../SubstepConfirmationModal/SubstepConfirmationModal';

import HeaderActions from './components/HeaderActions';
import { DEFAULT_COLUMNS } from './lib/helpers';

const OnboardingFacilityTable = ({
  updateConfigs,
  setHeaderButtonProps,
  onNext,
  onPrev,
  mutateOrchestratorData,
  orchestratorLoading
}) => {
  const { practice } = useOutletContext();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { data: facilityData } = useFacilityProvider({
    params: { practiceId: practice?.id },
    dependencies: [practice?.id]
  });

  const hasFacilities = (facilityData?.facilityInfo ?? facilityData ?? [])?.length > 0;

  useEffect(() => {
    updateConfigs({
      message: 'After adding facilities you can submit the data.',
      goBackHandler: onPrev,
      actions: [
        {
          text: 'Continue',
          onClick: async () => {
            if (!hasFacilities) {
              setShowConfirmModal(true);
              return;
            }
            mutateOrchestratorData();
          },
          isDisabled: orchestratorLoading
        }
      ]
    });
  }, [orchestratorLoading, hasFacilities]);

  const renderEmptyState = useCallback(
    () => (
      <EmptyDataRows
        title="No facilities added yet"
        description="Press 'New facility' to add your first service location"
        emptyTextButton="New facility"
      />
    ),
    []
  );

  return (
    <div className="mb-4 flex flex-1 flex-col">
      <AgTablePage
        defaultColumns={DEFAULT_COLUMNS}
        defaultFilters={[]}
        useData={useFacilityProvider}
        title="Facilities"
        name="facilityInfo"
        noHeader
        actions={(props) => (
          <HeaderActions {...props} setMotionHeaderButtonProps={setHeaderButtonProps} />
        )}
        noRowsOverlayComponent={renderEmptyState}
        rowStyle={{ cursor: 'pointer' }}
        queryParams={{ practiceId: practice?.id }}
        ModalComponent={CreateUpdateFacilityProvider}
        autoHeight
        className="overflow-y-auto rounded-2xl border border-solid border-neutral-200"
      />
      <SubstepConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onMarkComplete={() => {
          setShowConfirmModal(false);
          mutateOrchestratorData({ skipFacilities: true });
        }}
        onSkip={() => {
          setShowConfirmModal(false);
          onNext();
        }}
        title="No facilities added"
        message="You haven't added any facilities yet. Choose how you'd like to proceed before moving on."
        isLoading={orchestratorLoading}
      />
    </div>
  );
};

export default OnboardingFacilityTable;
